import { useEffect, useRef } from 'react';

/**
 * Helper to dispatch native value changes to React controlled inputs
 */
const setNativeInputValue = (element, value) => {
  if (!element) return;
  try {
    const safeVal = value === null || value === undefined ? '' : String(value);
    const valueSetter = Object.getOwnPropertyDescriptor(element, 'value')?.set;
    const prototype = Object.getPrototypeOf(element);
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

    if (element._valueTracker) {
      element._valueTracker.setValue('');
    }

    if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
      prototypeValueSetter.call(element, safeVal);
    } else if (valueSetter) {
      valueSetter.call(element, safeVal);
    } else {
      element.value = safeVal;
    }
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  } catch (err) {
    try {
      element.value = value || '';
    } catch (e) {}
  }
};

/**
 * Custom React Hook for hardware barcode scanner keyboard listening.
 *
 * Uses refs for scanner buffer, timing, and callback so that component
 * re-renders (e.g. from state changes) NEVER interrupt or erase an in-flight scan burst.
 *
 * Hardware barcode scanners emit keystrokes at very high speeds (< 50ms per key)
 * terminated by an 'Enter' key.
 *
 * NOTE: Individual character keydowns are NEVER prevented, allowing normal human typing
 * at any speed without keystroke drops or lag. If a barcode scan happens while an input
 * is focused, any characters that entered the input are restored to their pre-scan value
 * when 'Enter' arrives.
 *
 * @param {Object} options
 * @param {Function} options.onScan Callback function invoked with the scanned barcode string.
 * @param {String} [options.allowedInputName] If set, scan is only processed when activeElement name === allowedInputName.
 * @param {Boolean} [options.isCapture=false] If true, attaches window keydown listener with event capture phase.
 * @param {Number} [options.minBufferLength=3] Minimum characters required for valid barcode scan.
 * @param {Number} [options.maxIntervalMs=100] Maximum keydown interval (ms) between characters for scanner input.
 * @param {Array} [options.deps=[]] Optional dependency array.
 */
export const useBarcodeScanner = ({
  onScan,
  allowedInputName = null,
  isCapture = false,
  minBufferLength = 3,
  maxIntervalMs = 120,
  deps = [],
}) => {
  const onScanRef = useRef(onScan);
  const allowedInputNameRef = useRef(allowedInputName);
  const minBufferLengthRef = useRef(minBufferLength);
  const maxIntervalMsRef = useRef(maxIntervalMs);

  useEffect(() => {
    onScanRef.current = onScan;
    allowedInputNameRef.current = allowedInputName;
    minBufferLengthRef.current = minBufferLength;
    maxIntervalMsRef.current = maxIntervalMs;
  });

  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const intervalsRef = useRef([]);
  const targetInputRef = useRef(null);
  const targetValueRef = useRef(null);

  useEffect(() => {
    const isTextInput = (el) => {
      if (!el) return false;
      const tag = el.tagName ? el.tagName.toUpperCase() : '';
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const getEventTime = (e) => {
      if (typeof e?.timeStamp === 'number' && e.timeStamp > 0) {
        return e.timeStamp;
      }
      if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return performance.now();
      }
      return Date.now();
    };

    const resetBuffer = () => {
      bufferRef.current = '';
      intervalsRef.current = [];
      targetInputRef.current = null;
      targetValueRef.current = null;
      lastKeyTimeRef.current = 0;
    };

    const handleGlobalKeyDown = (e) => {
      const activeEl = document.activeElement;

      // If allowedInputName is specified, restrict scanner processing strictly to that field
      const currentAllowedName = allowedInputNameRef.current;
      if (currentAllowedName) {
        const inputName = activeEl ? activeEl.getAttribute('name') : '';
        if (inputName !== currentAllowedName) {
          resetBuffer();
          return;
        }
      }

      // Non-character navigation/editing keys indicate human editing, not a scanner burst
      if (
        ['Backspace', 'Delete', 'Escape', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(
          e.key
        )
      ) {
        resetBuffer();
        return;
      }

      const currentTime = getEventTime(e);
      const inInput = isTextInput(activeEl);
      // Hardware barcode scanners send characters very fast (< 50ms).
      // Humans type slower (> 150ms).
      // When inside an input, use a resilient interval limit (120ms) so that React rendering
      // triggered by the first character does not wipe the buffer before the second character arrives.
      const intervalLimit = maxIntervalMsRef.current || (inInput ? 120 : 150);

      if (e.key === 'Enter') {
        const enterDelay =
          lastKeyTimeRef.current > 0 && currentTime >= lastKeyTimeRef.current
            ? currentTime - lastKeyTimeRef.current
            : 99999;
        const minLength = inInput ? Math.max(3, minBufferLengthRef.current || 3) : (minBufferLengthRef.current || 2);
        const scannedCode = bufferRef.current.trim();

        let isValidScan = false;
        if (scannedCode.length >= minLength) {
          if (inInput) {
            const intervals = intervalsRef.current;
            const avgInterval =
              intervals.length > 0 ? intervals.reduce((sum, v) => sum + v, 0) / intervals.length : 0;
            // For an active input: barcode scanners have avgInterval <= 75ms and enterDelay <= 250ms
            if (avgInterval <= 75 && enterDelay <= 250) {
              isValidScan = true;
            }
          } else {
            // Outside of inputs (e.g. focused on table or body in Sales/Purchase page)
            if (enterDelay <= 300) {
              isValidScan = true;
            }
          }
        }

        if (isValidScan && scannedCode) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') {
            e.stopImmediatePropagation();
          }

          // Clean up any characters that entered the active input before scan was confirmed
          if (targetInputRef.current && targetValueRef.current !== null) {
            const currentInputName = targetInputRef.current.getAttribute('name');
            if (
              currentInputName !== 'ean' &&
              currentInputName !== 'barcode' &&
              targetInputRef.current.value !== targetValueRef.current
            ) {
              setNativeInputValue(targetInputRef.current, targetValueRef.current);
            }
            if (currentInputName !== 'ean' && currentInputName !== 'barcode') {
              try {
                targetInputRef.current.blur();
              } catch (err) {}
            }
          }

          if (typeof onScanRef.current === 'function') {
            onScanRef.current(scannedCode, targetInputRef.current?.getAttribute('name'));
          }

          resetBuffer();
          return;
        }

        resetBuffer();
        return;
      }

      // Single printable characters
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        const interval =
          lastKeyTimeRef.current > 0 && currentTime >= lastKeyTimeRef.current
            ? currentTime - lastKeyTimeRef.current
            : 99999;

        if (interval > intervalLimit) {
          // Exceeded scanner interval threshold -> start a fresh buffer
          bufferRef.current = '';
          intervalsRef.current = [];
          targetInputRef.current = inInput && ('value' in activeEl) ? activeEl : null;
          targetValueRef.current = targetInputRef.current ? targetInputRef.current.value : null;
        } else if (lastKeyTimeRef.current > 0) {
          intervalsRef.current.push(interval);
        }

        bufferRef.current += e.key;
        lastKeyTimeRef.current = currentTime;

        // CRITICAL: DO NOT call e.preventDefault() here!
        // Preventing default on keydown breaks normal typing at speed in text inputs.
        // Barcode scans will be cleanly validated and handled on 'Enter', with any leaked input restored.
      }
    };

    const handleMouseDown = () => {
      // Mouse click indicates manual human interaction, so reset scanner buffer
      resetBuffer();
    };

    window.addEventListener('keydown', handleGlobalKeyDown, isCapture);
    window.addEventListener('mousedown', handleMouseDown, true);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, isCapture);
      window.removeEventListener('mousedown', handleMouseDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCapture, ...deps]);
};

export default useBarcodeScanner;
